import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../client';

export const fetchInstantSurveys = createAsyncThunk(
  'instantSurveys/getInstantSurveys',
  async ({ currentAccountUuid }, { rejectWithValue }) => {
    try {
      if (!currentAccountUuid) {
        return rejectWithValue({
          error: 'No valid account IDs provided',
          status: 400,
        });
      }

      // Call new API endpoint
      const response = await client.get(`/accounts/${currentAccountUuid}`);

      if (response.ok) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        // Handle validation errors from the API
        if (response.data?.errors && Array.isArray(response.data.errors)) {
          const validationErrors = {};
          response.data.errors.forEach(error => {
            validationErrors[error.field] = error.message;
          });

          return rejectWithValue({
            error: 'Validation failed',
            status: response.status,
            validationErrors,
          });
        }

        return rejectWithValue({
          error: response.data?.message || 'Failed to remove user from accounts',
          status: response.status,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Network error occurred',
        status: 500,
      });
    }
  }
);

export const fetchInstantSurveysData = createAsyncThunk(
  'populationHealth/fetchSurveysData',
  async ({ accountUuid }, { rejectWithValue }) => {
    try {
      // Call the surveys-data API endpoint
      const response = await client.get(`/accounts/${accountUuid}/surveys-data`);

      if (response.ok) {
        const data = response.data;

        // Transform survey types to assessment instruments format
        // Extract from data.data.result array
        const assessmentTypes = data.data?.result || [];

        // Add "All Assessments" option at the beginning
        const instruments = [
          // { id: 'all', name: 'All Assessments' },
          ...assessmentTypes.map(survey => ({
            id: survey.id,
            name: survey.title,
            isInstantSurvey: survey.isInstantSurvey,
            description: survey.description || ''
          }))
        ];

        return {
          ok: true,
          data: {
            instruments,
            rawSurveys: assessmentTypes
          },
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to load survey types',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to load survey types',
        },
      });
    }
  }
);

export const createInstantSurvey = createAsyncThunk(
  'instantSurveys/createInstantSurvey',
  async ({ accountUuid, newSurvey, existingSurveys }, { rejectWithValue }) => {
    try {
      // Prepare the updated surveys list with new survey at the beginning
      const updatedSurveys = [newSurvey, ...existingSurveys];

      // Prepare JSON Patch operation
      const patchOperations = [
        {
          op: 'replace',
          path: '/instantSurveys',
          value: updatedSurveys
        }
      ];

      // Make PATCH request to update instant surveys
      const response = await client.patch(
        `/accounts/${accountUuid}`,
        patchOperations
      );

      if (response.ok) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return rejectWithValue({
          error: response.data?.message || 'Failed to create instant survey',
          status: response.status,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Network error occurred',
        status: 500,
      });
    }
  }
);

// Update instant survey (for future editing functionality)
export const updateInstantSurvey = createAsyncThunk(
  'instantSurveys/updateInstantSurvey',
  async ({ accountUuid, updatedSurvey, existingSurveys }, { rejectWithValue }) => {
    try {
      // Find and replace the survey in the list
      const updatedSurveys = existingSurveys.map(survey =>
        survey.key === updatedSurvey.key ? updatedSurvey : survey
      );

      // Prepare JSON Patch operation
      const patchOperations = [
        {
          op: 'replace',
          path: '/instantSurveys',
          value: updatedSurveys
        }
      ];

      // Make PATCH request to update instant surveys
      const response = await client.patch(
        `/accounts/${accountUuid}`,
        patchOperations
      );

      if (response.ok) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return rejectWithValue({
          error: response.data?.message || 'Failed to update instant survey',
          status: response.status,
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: error.message || 'Network error occurred',
        status: 500,
      });
    }
  }
);
